import { BizyPageContext } from '../store/use-bizy-page-context-store';

interface BizyRouteEntry {
	prefix?: string;
	/** Regex alternative to prefix — used for dynamic segments (e.g. /team/:id/meet). */
	match?: RegExp;
	context: BizyPageContext;
	/** Optional override that can read regex capture groups to produce dynamic context (e.g. meeting_partner_id from the URL). */
	buildContext?: (match: RegExpMatchArray) => BizyPageContext;
	/** Short label for conversation history (defaults to context.pageName) */
	shortName?: string;
}

const HOME_CONTEXT: BizyPageContext = { pageName: 'Home', pageType: 'home' };
const DEFAULT_CONTEXT: BizyPageContext = { pageName: 'Home', pageType: 'home' };

/**
 * Declarative mapping from route prefixes to Bizy page context.
 *
 * Order matters: more specific prefixes must appear before their parents
 * (e.g. `/growth/goals` before `/growth`).
 *
 * When adding Bizy support for a new page, add an entry here.
 */
export const BIZY_ROUTE_CONFIG: BizyRouteEntry[] = [
	// Admin pages (Kaleidoscope SPA) — more specific prefixes first.
	{
		prefix: '/admin/reports/participation',
		context: { pageName: 'Participation Report', pageType: 'admin', pageData: { admin_page: 'participation' } },
		shortName: 'Participation',
	},
	{
		prefix: '/admin/reports/points-activity',
		context: { pageName: 'Points Activity Report', pageType: 'admin', pageData: { admin_page: 'points_activity' } },
		shortName: 'Points Activity',
	},
	{
		prefix: '/admin/reports/collaboration',
		context: { pageName: 'Collaboration Report', pageType: 'admin', pageData: { admin_page: 'collaboration' } },
		shortName: 'Collaboration',
	},
	{
		prefix: '/admin/reports/recognition_scores',
		context: { pageName: 'Recognition Scores', pageType: 'admin', pageData: { admin_page: 'recognition_scores' } },
		shortName: 'Recognition Scores',
	},
	{
		prefix: '/admin/reports/pulse-results',
		context: { pageName: 'Pulse Results', pageType: 'admin', pageData: { admin_page: 'pulse_results' } },
		shortName: 'Pulse Results',
	},
	{ prefix: '/admin/reports', context: { pageName: 'Reports', pageType: 'admin' } },
	{ prefix: '/admin/dashboard', context: { pageName: 'Dashboard', pageType: 'admin', pageData: { admin_page: 'dashboard' } } },
	{ prefix: '/admin/growth', context: { pageName: 'Growth', pageType: 'admin' } },
	{ prefix: '/admin/company', context: { pageName: 'Company', pageType: 'admin' } },
	{ prefix: '/admin/rewards', context: { pageName: 'Company', pageType: 'admin' } },
	{
		prefix: '/admin/incentives',
		context: { pageName: 'Incentives Summary', pageType: 'admin', pageData: { admin_page: 'incentives' } },
		shortName: 'Incentives',
	},
	{ prefix: '/admin/subscriptions', context: { pageName: 'Reports', pageType: 'admin' } },
	{ prefix: '/admin/programs/settings', context: { pageName: 'Company', pageType: 'admin' } },
	{ prefix: '/admin/programs', context: { pageName: 'Recognition', pageType: 'admin' } },
	{ prefix: '/admin/awards', context: { pageName: 'Recognition', pageType: 'admin' } },
	{ prefix: '/admin', context: { pageName: 'Admin', pageType: 'admin' } },

	// Rails admin paths — mapped to admin sidebar menu sections.
	// More specific prefixes must come before their parents.

	// Users section
	{ prefix: '/company/integrations/user_managements', context: { pageName: 'Users', pageType: 'admin' } },
	{ prefix: '/company/imported_users', context: { pageName: 'Users', pageType: 'admin' } },
	{ prefix: '/company/users/participation', context: { pageName: 'Reports', pageType: 'admin' } },
	{ prefix: '/company/users', context: { pageName: 'Users', pageType: 'admin' } },

	// Recognition section (Rails)
	{ prefix: '/awards/approval_requests', context: { pageName: 'Recognition', pageType: 'admin' } },
	{ prefix: '/company/bonus_reports', context: { pageName: 'Recognition', pageType: 'admin' } },

	// Reports section (Rails)
	{ prefix: '/company/reports', context: { pageName: 'Reports', pageType: 'admin' } },
	{ prefix: '/company/rewards/totals_report', context: { pageName: 'Reports', pageType: 'admin' } },
	{ prefix: '/company/rewards', context: { pageName: 'Reports', pageType: 'admin' }, shortName: 'Redemptions' },
	{ prefix: '/company/accounts', context: { pageName: 'Reports', pageType: 'admin' } },

	// Company section (Rails)
	{ prefix: '/company/settings', context: { pageName: 'Company', pageType: 'admin' } },
	{ prefix: '/company/billing_activity', context: { pageName: 'Company', pageType: 'admin' } },
	{ prefix: '/company/billing', context: { pageName: 'Company', pageType: 'admin' } },

	// Legacy Analytics (Rails)
	{ prefix: '/analytics', context: { pageName: 'Analytics', pageType: 'admin' } },

	// Super Admin (Rails)
	{ prefix: '/super_admin', context: { pageName: 'Super Admin', pageType: 'admin' } },

	// Tech Ops (Rails)
	{ prefix: '/tech_ops', context: { pageName: 'Tech Ops', pageType: 'admin' } },

	// Team-scoped sub-pages (must come before the /team catch-all).
	// Both the permalink (/team/:userId/meet) and the explicit meeting
	// subroute render the 1:1 meeting view, so both resolve to pageType
	// '1on1' and surface meeting_partner_id (and meeting_id when present)
	// so Bizy can infer who the 1:1 is with without asking.
	{
		match: /^\/team\/([^/]+)\/meet(?:\/([^/]+))?\/?$/,
		context: { pageName: '1:1 Meeting', pageType: '1on1' },
		buildContext: (m) => ({
			pageName: '1:1 Meeting',
			pageType: '1on1',
			pageData: {
				meeting_partner_id: m[1],
				...(m[2] ? { meeting_id: m[2] } : {}),
			},
		}),
		shortName: '1:1',
	},
	{ match: /^\/team\/[^/]+\/goals(\/|$)/, context: { pageName: 'Goals', pageType: 'goals' } },
	{ match: /^\/team\/[^/]+\/recap-review(\/|$)/, context: { pageName: 'Recaps', pageType: 'recaps' } },
	{ match: /^\/team\/[^/]+\/feedback(\/|$)/, context: { pageName: 'Advice', pageType: 'advice' } },

	// Top-level meeting routes (individual before list)
	{ match: /^\/meet\/[^/]/, context: { pageName: '1:1 Meeting', pageType: '1on1' }, shortName: '1:1' },
	{ match: /^\/meet\/?$/, context: { pageName: '1:1 Meetings', pageType: '1on1_list' }, shortName: '1:1s' },

	// Growth sub-pages (specific before parent; bare /growth falls through to home)
	{ prefix: '/growth/goals', context: { pageName: 'Goals', pageType: 'goals' } },
	{ prefix: '/growth/recap-review', context: { pageName: 'Recaps', pageType: 'recaps' } },
	{ prefix: '/growth/feedback', context: { pageName: 'Advice', pageType: 'advice' } },

	{ prefix: '/incentives', context: { pageName: 'Incentives', pageType: 'incentives' } },
	{ prefix: '/rewards', context: { pageName: 'Rewards', pageType: 'rewards' } },
	{ prefix: '/user/settings', context: { pageName: 'Settings', pageType: 'settings' } },
	{ prefix: '/team', context: { pageName: 'Team', pageType: 'team' } },
	{ prefix: '/bookmark', context: { pageName: 'Bookmarks', pageType: 'bookmarks' } },
	{ prefix: '/bonuses', context: HOME_CONTEXT },
	{ prefix: '/invite', context: HOME_CONTEXT },
];

function matchRoute(pathname: string, route: BizyRouteEntry): RegExpMatchArray | true | null {
	if (route.match) return pathname.match(route.match);
	if (route.prefix) return pathname.startsWith(route.prefix) ? true : null;
	return null;
}

export function resolvePageContext(pathname: string): BizyPageContext {
	if (pathname === '/') return HOME_CONTEXT;

	for (const route of BIZY_ROUTE_CONFIG) {
		const result = matchRoute(pathname, route);
		if (!result) continue;
		if (route.buildContext && result !== true) return route.buildContext(result);
		return route.context;
	}

	return DEFAULT_CONTEXT;
}

/** Short display label for a route path, used in conversation history lists. */
export function getPageDisplayName(pathname: string | null): string {
	if (!pathname) return 'Bonusly';
	if (pathname === '/') return 'Home';

	for (const route of BIZY_ROUTE_CONFIG) {
		if (matchRoute(pathname, route)) {
			return route.shortName ?? route.context.pageName;
		}
	}

	return 'Bonusly';
}
